import {defineStore} from 'pinia'
import {$$, $computed, $ref} from '@vue-macros/reactivity-transform/macros'
import {ITaskItemConfig} from '@toolify/client/src/stores/TaskStore/types/ITaskItemConfig'
import {ITaskItem} from '@toolify/client/src/stores/TaskStore/types/ITaskItem'
import {TaskItemStatus} from '@toolify/client/src/stores/TaskStore/enum/TaskItemStatus'
import {ITaskItemProgress} from '@toolify/client/src/stores/TaskStore/types/ITaskItemProgress'
import {computed, watch} from 'vue'
import {TaskType} from '@toolify/client/src/stores/TaskStore/enum/TaskType'
import {TaskTypeStrategyMap} from '@toolify/client/src/stores/TaskStore/strategies/TaskTypeStrategyMap'
import {ITaskTypeStrategy} from '@toolify/client/src/stores/TaskStore/strategies/ITaskTypeStrategy'
import {createUploadPauseController} from '@toolify/client/src/modules/files/upload/createUploadPauseController'
import axios from 'axios'

const MAX_SIMULTANEOUS_TASKS = 2
const SPEED_SAMPLE_LIMIT = 8
const SPEED_SAMPLE_MIN_DT_SECONDS = 0.25
const FIRST_SPEED_SAMPLE_MIN_DT_SECONDS = 0.05

export const useTaskStore = defineStore('task', () => {
  let tasks = $ref<ITaskItem[]>([])
  const lastByteReportByTaskId = new Map<symbol, {bytesTransferred: number, timestampMs: number}>()
  let speedSamplesByType = $ref<Record<TaskType, number[]>>({
    [TaskType.Upload]: [],
    [TaskType.Download]: [],
    [TaskType.Generic]: [],
  })

  function createInitialProgress(taskConfig: ITaskItemConfig): ITaskItemProgress {
    return taskConfig.initialProgress
      ? {...taskConfig.initialProgress}
      : {
        percentage: 0,
        bytesTransferred: 0,
      }
  }

  function addTask(taskConfig: ITaskItemConfig) {
    return new Promise((resolve) => {
      const type = taskConfig.type ?? TaskType.Generic
      const payload = (taskConfig.payload ?? null) as ITaskItem['payload']
      tasks.push({
        id: Symbol(),
        type,
        payload,
        handle: taskConfig.handle,
        config: taskConfig,
        status: TaskItemStatus.Queued,
        onFinished(output: unknown) {
          resolve(output)
        },
        onSettled: taskConfig.onSettled,
        onFailed: taskConfig.onFailed,
        progress: createInitialProgress(taskConfig),
        abortController: new AbortController(),
        pauseController: createUploadPauseController(),
      })
      handleTasks()
    })

  }

  function handleTasks() {
    tasks = [...tasks]
    if(!tasks.length) {
      return
    }
    const startedTaskCount = tasks.filter(task => {
      return task.status === TaskItemStatus.Started
    }).length
    const tasksToHandle = tasks.filter(task => {
      return task.status === TaskItemStatus.Queued
    }).slice(0, MAX_SIMULTANEOUS_TASKS - startedTaskCount)
    if(!tasks.length) {
      return
    }
    for(const task of tasksToHandle) {
      startTask(task)
    }
  }

  function startTask(task: ITaskItem) {
    task.status = TaskItemStatus.Started
    const taskItemActions = {
      setProgress(progress: ITaskItemProgress) {
        setTaskProgress(task.id, progress)
      },
      updatePayload(patch: Record<string, unknown>) {
        updateTaskPayload(task.id, patch)
      },
      signal: task.abortController.signal,
      pauseController: task.pauseController,
    }
    task.handle(task, taskItemActions).then(output => {
      task.status = TaskItemStatus.Finished
      task.progress.percentage = 1
      if(typeof task.progress.totalBytes === 'number') {
        task.progress.bytesTransferred = task.progress.totalBytes
      }
      task.error = null
      lastByteReportByTaskId.delete(task.id)
      task.onFinished(output)
      settleTask(task)
      handleTasks()
    }).catch(error => {
      const isCancelled = axios.isCancel(error) || error?.name === 'CanceledError' || error?.name === 'AbortError' || task.abortController.signal.aborted
      task.status = isCancelled ? TaskItemStatus.Cancelled : TaskItemStatus.Failed
      task.error = isCancelled ? null : error
      lastByteReportByTaskId.delete(task.id)
      task.onFinished(undefined)
      if(isCancelled) {
        settleTask(task)
      } else {
        task.onFailed?.(error, task.id)
      }
      handleTasks()
    })
  }

  // a failed task stays unsettled on purpose: its owner keeps the optimistic UI alive until retryTask or dismissTask
  function settleTask(task: ITaskItem) {
    if(task.hasSettled) {
      return
    }
    task.hasSettled = true
    task.onSettled?.()
  }

  function retryTask(taskId: symbol) {
    const task = findTaskById(taskId)
    if(!task || task.status !== TaskItemStatus.Failed) {
      return
    }
    task.status = TaskItemStatus.Queued
    task.error = null
    task.payload = (task.config.payload ?? null) as ITaskItem['payload']
    task.progress = createInitialProgress(task.config)
    task.abortController = new AbortController()
    lastByteReportByTaskId.delete(task.id)
    handleTasks()
  }

  function dismissTask(taskId: symbol) {
    const task = findTaskById(taskId)
    if(!task) {
      return
    }
    if([TaskItemStatus.Queued, TaskItemStatus.Started].includes(task.status)) {
      task.abortController.abort()
      task.status = TaskItemStatus.Cancelled
    }
    lastByteReportByTaskId.delete(task.id)
    task.onFinished(undefined)
    settleTask(task)
    tasks = tasks.filter(otherTask => {
      return otherTask.id !== task.id
    })
    handleTasks()
  }

  function dismissAllFailedTasks() {
    for(const task of failedTasks.value) {
      dismissTask(task.id)
    }
  }

  function dismissAllTransferTasks() {
    const taskIds = transferTasks.value.map(task => task.id)
    for(const taskId of taskIds) {
      dismissTask(taskId)
    }
  }

  function retryAllFailedTasks() {
    const taskIds = failedTasks.value.map(task => task.id)
    for(const taskId of taskIds) {
      retryTask(taskId)
    }
  }

  function cancelTask(taskId: symbol) {
    const task = findTaskById(taskId)
    if(!task) {
      return
    }
    task.abortController.abort()
    if(task.status === TaskItemStatus.Queued) {
      task.status = TaskItemStatus.Cancelled
      lastByteReportByTaskId.delete(task.id)
      task.onFinished(undefined)
      settleTask(task)
      handleTasks()
    }
  }

  function pauseTask(taskId: symbol) {
    findTaskById(taskId)?.pauseController.pause()
    handleTasks()
  }

  function resumeTask(taskId: symbol) {
    findTaskById(taskId)?.pauseController.resume()
    handleTasks()
  }

  function pauseAllTransferTasks() {
    for(const task of pausableTransferTasks.value) {
      task.pauseController.pause()
    }
    handleTasks()
  }

  function resumeAllTransferTasks() {
    for(const task of pausableTransferTasks.value) {
      task.pauseController.resume()
    }
    handleTasks()
  }

  function cancelAllTransferTasks() {
    for(const task of tasks) {
      if(![TaskType.Upload, TaskType.Download].includes(task.type)) {
        continue
      }
      if([TaskItemStatus.Finished, TaskItemStatus.Failed, TaskItemStatus.Cancelled].includes(task.status)) {
        continue
      }
      cancelTask(task.id)
    }
  }

  function findTaskById(taskId: symbol) {
    return tasks.find(task => {
      return task.id === taskId
    })
  }

  function setTaskProgress(taskId: symbol, progress: ITaskItemProgress) {
    const task = findTaskById(taskId)
    if(!task) {
      return
    }
    task.progress = {
      ...task.progress,
      ...progress,
    }

    const nowMs = Date.now()
    if(typeof task.progress.bytesTransferred === 'number') {
      const last = lastByteReportByTaskId.get(taskId)
      if(last) {
        const dtSeconds = (nowMs - last.timestampMs) / 1000
        const deltaBytes = task.progress.bytesTransferred - last.bytesTransferred
        const existingSamples = speedSamplesByType[task.type] || []
        const minDtSeconds = existingSamples.length
          ? SPEED_SAMPLE_MIN_DT_SECONDS
          : FIRST_SPEED_SAMPLE_MIN_DT_SECONDS
        if(dtSeconds >= minDtSeconds && deltaBytes > 0) {
          const speed = deltaBytes / dtSeconds
          speedSamplesByType = {
            ...speedSamplesByType,
            [task.type]: [...existingSamples.slice(-(SPEED_SAMPLE_LIMIT - 1)), speed],
          }
          // Only advance the last marker when we actually record a sample.
          // Otherwise, very frequent progress events can prevent dt from ever reaching the threshold.
          lastByteReportByTaskId.set(taskId, {
            bytesTransferred: task.progress.bytesTransferred,
            timestampMs: nowMs,
          })
        }
      } else {
        // first report: establish baseline
        lastByteReportByTaskId.set(taskId, {
          bytesTransferred: task.progress.bytesTransferred,
          timestampMs: nowMs,
        })
      }
    }
    handleTasks()
  }

  function updateTaskPayload(taskId: symbol, patch: Record<string, unknown>) {
    const task = findTaskById(taskId)
    if(!task) {
      return
    }
    task.payload = {
      ...(task.payload || {}),
      ...(patch || {}),
    } as ITaskItem['payload']
    handleTasks()
  }

  function clearTasksByStatus(status: TaskItemStatus) {
    tasks = tasks.filter(task => {
      return task.status !== status || task.status === TaskItemStatus.Started
    })
  }

  const hasAnyTasks = $computed(() => {
    return !!tasks.length
  })

  const hasAnyStartedTasks = $computed(() => {
    return tasks.findIndex(task => {
      return task.status === TaskItemStatus.Started
    }) !== -1
  })

  const totalProgressPercentage = $computed(() => {
    const includedTasks = tasks.filter(task => {
      return ![TaskItemStatus.Failed, TaskItemStatus.Cancelled].includes(task.status)
    })
    if(!includedTasks.length) {
      return 0
    }
    const totalProgress = includedTasks.reduce((totalProgress, task) => {
      return totalProgress + task.progress.percentage
    }, 0)
    return totalProgress / includedTasks.length
  })

  const hasAnyUnfinishedTasks = $computed(() => {
    return tasks.findIndex(task => {
      return ![TaskItemStatus.Finished, TaskItemStatus.Cancelled, TaskItemStatus.Failed].includes(task.status)
    }) !== -1
  })

  const failedTasks = computed<ITaskItem[]>(() => {
    return tasks.filter(task => {
      return task.status === TaskItemStatus.Failed
    })
  })

  // everything the transfer panel lists, finished ones included so a completed row stays visible for a moment
  const transferTasks = computed<ITaskItem[]>(() => {
    return tasks.filter(task => {
      return isTransferTask(task) && task.status !== TaskItemStatus.Cancelled
    })
  })

  const pausableTransferTasks = computed<ITaskItem[]>(() => {
    return tasks.filter(task => {
      return isTransferTask(task)
        && task.status === TaskItemStatus.Started
        && task.pauseController.isSupported
    })
  })

  const hasAnyPausableTransferTasks = computed(() => {
    return !!pausableTransferTasks.value.length
  })

  const areAllPausableTransferTasksPaused = computed(() => {
    return !!pausableTransferTasks.value.length && pausableTransferTasks.value.every(task => {
      return task.pauseController.isPaused
    })
  })

  const hasAnyFailedTasks = computed(() => {
    return !!failedTasks.value.length
  })

  const hasAnyNonFailedTasks = computed(() => {
    return tasks.some(task => {
      return task.status !== TaskItemStatus.Failed
    })
  })

  let clearSettledTasksTimeout: ReturnType<typeof setTimeout> = null

  // remove tasks after 5 seconds if all are complete
  watch($$(hasAnyUnfinishedTasks), (value) => {
    clearTimeout(clearSettledTasksTimeout)
    if(value) {
      return
    }
    clearSettledTasksTimeout = setTimeout(() => {
      clearSettledTasks()
    }, 5000)
  })

  // failed tasks survive the sweep so the user can still retry or dismiss them
  function clearSettledTasks() {
    tasks = tasks.filter(task => {
      return task.status !== TaskItemStatus.Finished && task.status !== TaskItemStatus.Cancelled
    })
  }

  function getAverageSpeedBytesPerSecond(type: TaskType): number | null {
    const samples = speedSamplesByType[type] || []
    if(!samples.length) {
      return null
    }
    const total = samples.reduce((acc, v) => acc + v, 0)
    return total / samples.length
  }

  function isTransferTask(task: ITaskItem) {
    return [TaskType.Upload, TaskType.Download].includes(task.type)
  }

  const hasAnyActiveTransferTasks = $computed(() => {
    return tasks.some(task => {
      return isTransferTask(task) && ![TaskItemStatus.Finished, TaskItemStatus.Failed, TaskItemStatus.Cancelled].includes(task.status)
    })
  })

  const hasAnyStartedTransferTasks = $computed(() => {
    return tasks.some(task => {
      return isTransferTask(task) && task.status === TaskItemStatus.Started
    })
  })

  const fileTransferEstimatedRemainingSeconds = $computed<number | null>(() => {
    const transferTasks = tasks.filter(task => {
      return isTransferTask(task) && ![TaskItemStatus.Finished, TaskItemStatus.Failed, TaskItemStatus.Cancelled].includes(task.status)
    })
    if(!transferTasks.length) {
      return null
    }

    const durationsById = new Map<symbol, number>()
    for(const task of transferTasks) {
      const strategy = TaskTypeStrategyMap.get(task.type) as ITaskTypeStrategy<typeof task.type> | undefined
      const averageSpeed = getAverageSpeedBytesPerSecond(task.type)
      const seconds = strategy?.calculateTaskTime(task as ITaskItem<typeof task.type>, tasks, {
        averageSpeedBytesPerSecond: averageSpeed,
      }) ?? null
      if(typeof seconds !== 'number' || !isFinite(seconds) || seconds < 0) {
        return null
      }
      durationsById.set(task.id, seconds)
    }

    const workerTimes = new Array(MAX_SIMULTANEOUS_TASKS).fill(0)
    const tasksInOrder = transferTasks
      .slice()
      .sort((a, b) => {
        // started tasks first (they already occupy slots), then queued
        const aRank = a.status === TaskItemStatus.Started ? 0 : 1
        const bRank = b.status === TaskItemStatus.Started ? 0 : 1
        if(aRank !== bRank) return aRank - bRank
        // preserve original order
        return tasks.findIndex(t => t.id === a.id) - tasks.findIndex(t => t.id === b.id)
      })

    const pickLeastBusyWorkerIndex = () => {
      let bestIndex = 0
      for(let i = 1; i < workerTimes.length; i++) {
        if(workerTimes[i] < workerTimes[bestIndex]) {
          bestIndex = i
        }
      }
      return bestIndex
    }

    for(const task of tasksInOrder) {
      const seconds = durationsById.get(task.id) || 0
      const idx = pickLeastBusyWorkerIndex()
      workerTimes[idx] += seconds
    }

    return Math.max(...workerTimes)
  })

  return {
    ...$$({
      addTask,
      clearTasksByStatus,
      cancelTask,
      cancelAllTransferTasks,
      hasAnyTasks,
      hasAnyStartedTasks,
      hasAnyActiveTransferTasks,
      hasAnyStartedTransferTasks,
      totalProgressPercentage,
      fileTransferEstimatedRemainingSeconds,
    }),
    failedTasks,
    hasAnyFailedTasks,
    hasAnyNonFailedTasks,
    transferTasks,
    hasAnyPausableTransferTasks,
    areAllPausableTransferTasksPaused,
    retryTask,
    retryAllFailedTasks,
    dismissTask,
    dismissAllFailedTasks,
    dismissAllTransferTasks,
    pauseTask,
    resumeTask,
    pauseAllTransferTasks,
    resumeAllTransferTasks,
  }
})
